import React, { useState, useRef, useEffect } from 'react';
import { CloseIcon } from './icons/CloseIcon';
import { LoadingSpinner } from './icons/LoadingSpinner';

// Icons for editor tools
const ZoomIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8" />
        <path d="M21 21l-4.35-4.35M11 8v6M8 11h6" />
    </svg>
);

const CropIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M6 2v4h12v12h4M2 6h4m0 0v16m12-4h4m-4 0V6" />
    </svg>
);

const ExtendIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
    </svg>
);

const AddElementIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M12 8v8M8 12h8" />
    </svg>
);

const EraseIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 20H9L4 15l10-10 7 7-6 6" />
        <path d="M14 5l5 5" />
    </svg>
);

const ReplaceIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M16 3h5v5M8 21H3v-5M21 3l-7 7M3 21l7-7" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const AnimateIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
);

const PlayIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5v14l11-7z" />
    </svg>
);

const PauseIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
    </svg>
);

const EDIT_TOOLS = [
    { id: 'zoom', name: 'Zoom & Pan', icon: ZoomIcon, description: 'Zoom in/out and pan across the video' },
    { id: 'crop', name: 'Crop', icon: CropIcon, description: 'Crop the video to a specific region' },
    { id: 'animate', name: 'Animate', icon: AnimateIcon, description: 'Add motion and animation effects' },
    { id: 'extend', name: 'Extend', icon: ExtendIcon, description: 'Extend or trim the video duration' },
    { id: 'addElement', name: 'Add Element', icon: AddElementIcon, description: 'Add objects, text, or overlays' },
    { id: 'eraseElement', name: 'Erase Element', icon: EraseIcon, description: 'Remove unwanted elements from video' },
    { id: 'replace', name: 'Replace', icon: ReplaceIcon, description: 'Replace characters or elements' },
];

export const VideoEditorModal = ({ 
    shot, 
    projectData,
    onClose, 
    onSave,
    onDeductCredits 
}) => {
    const [activeTool, setActiveTool] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [error, setError] = useState(null);
    const videoRef = useRef(null);
    
    // Tool-specific states
    const [zoomLevel, setZoomLevel] = useState(100);
    const [panX, setPanX] = useState(0);
    const [panY, setPanY] = useState(0);
    const [cropRegion, setCropRegion] = useState({ x: 0, y: 0, width: 100, height: 100 });
    const [trimStart, setTrimStart] = useState(0);
    const [trimEnd, setTrimEnd] = useState(100);
    const [editPrompt, setEditPrompt] = useState('');
    const [selectedElement, setSelectedElement] = useState(null);
    
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.addEventListener('loadedmetadata', () => {
                setDuration(videoRef.current.duration);
            });
            videoRef.current.addEventListener('timeupdate', () => {
                setCurrentTime(videoRef.current.currentTime);
            });
        }
    }, []);
    
    const handlePlayPause = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };
    
    const handleSeek = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;
        const time = percent * duration;
        if (videoRef.current) {
            videoRef.current.currentTime = time;
            setCurrentTime(time);
        }
    };
    
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };
    
    const handleApplyEdit = async () => {
        if (!activeTool) return;
        
        setIsProcessing(true);
        setError(null);
        
        try {
            // Prepare edit parameters based on active tool
            const editParams = {
                tool: activeTool,
                videoUrl: shot.video,
                shotId: shot.id,
            };
            
            switch (activeTool) {
                case 'zoom':
                    editParams.zoomLevel = zoomLevel;
                    editParams.panX = panX;
                    editParams.panY = panY;
                    break;
                case 'crop':
                    editParams.cropRegion = cropRegion;
                    break;
                case 'extend':
                    editParams.trimStart = trimStart;
                    editParams.trimEnd = trimEnd;
                    break;
                case 'animate':
                case 'addElement':
                case 'eraseElement':
                case 'replace':
                    editParams.prompt = editPrompt;
                    editParams.selectedElement = selectedElement;
                    break;
                default:
                    break;
            }
            
            console.log('🎬 Applying video edit:', editParams);
            
            // TODO: Call backend API to process the video edit
            // For now, show a placeholder message
            alert(`Video edit "${activeTool}" will be processed. This feature requires backend integration.`);
            
            // Simulate processing time
            await new Promise(resolve => setTimeout(resolve, 1000));
            
        } catch (err) {
            console.error('Video edit failed:', err);
            setError(err.message || 'Failed to apply edit');
        } finally {
            setIsProcessing(false);
        }
    };
    
    const renderToolPanel = () => {
        if (!activeTool) {
            return (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <p className="text-lg mb-2">Select a tool to start editing</p>
                    <p className="text-sm">Choose from the tools on the left</p>
                </div>
            );
        }
        
        switch (activeTool) {
            case 'zoom':
                return (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-purple-300">Zoom & Pan</h3>
                        <div>
                            <label className="block text-sm font-medium mb-2">Zoom Level: {zoomLevel}%</label>
                            <input
                                type="range"
                                min="50"
                                max="300"
                                value={zoomLevel}
                                onChange={(e) => setZoomLevel(parseInt(e.target.value))}
                                className="w-full accent-purple-500"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">Pan X: {panX}%</label>
                                <input
                                    type="range"
                                    min="-50"
                                    max="50"
                                    value={panX}
                                    onChange={(e) => setPanX(parseInt(e.target.value))}
                                    className="w-full accent-purple-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Pan Y: {panY}%</label>
                                <input
                                    type="range"
                                    min="-50"
                                    max="50"
                                    value={panY}
                                    onChange={(e) => setPanY(parseInt(e.target.value))}
                                    className="w-full accent-purple-500"
                                />
                            </div>
                        </div>
                        <button
                            onClick={() => { setZoomLevel(100); setPanX(0); setPanY(0); }}
                            className="text-sm text-gray-400 hover:text-white"
                        >
                            Reset to default
                        </button>
                    </div>
                );
                
            case 'crop':
                return (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-purple-300">Crop Video</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">X Position: {cropRegion.x}%</label>
                                <input
                                    type="range"
                                    min="0"
                                    max="50"
                                    value={cropRegion.x}
                                    onChange={(e) => setCropRegion(prev => ({ ...prev, x: parseInt(e.target.value) }))}
                                    className="w-full accent-purple-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Y Position: {cropRegion.y}%</label>
                                <input
                                    type="range"
                                    min="0"
                                    max="50"
                                    value={cropRegion.y}
                                    onChange={(e) => setCropRegion(prev => ({ ...prev, y: parseInt(e.target.value) }))}
                                    className="w-full accent-purple-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Width: {cropRegion.width}%</label>
                                <input
                                    type="range"
                                    min="20"
                                    max="100"
                                    value={cropRegion.width}
                                    onChange={(e) => setCropRegion(prev => ({ ...prev, width: parseInt(e.target.value) }))}
                                    className="w-full accent-purple-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Height: {cropRegion.height}%</label>
                                <input
                                    type="range"
                                    min="20"
                                    max="100"
                                    value={cropRegion.height}
                                    onChange={(e) => setCropRegion(prev => ({ ...prev, height: parseInt(e.target.value) }))}
                                    className="w-full accent-purple-500"
                                />
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setCropRegion({ x: 0, y: 0, width: 100, height: 100 })}
                                className="text-sm text-gray-400 hover:text-white"
                            >
                                Reset
                            </button>
                            <button
                                onClick={() => setCropRegion({ x: 12.5, y: 0, width: 75, height: 100 })}
                                className="text-sm bg-gray-700 px-2 py-1 rounded hover:bg-gray-600"
                            >
                                9:16
                            </button>
                            <button
                                onClick={() => setCropRegion({ x: 0, y: 12.5, width: 100, height: 75 })}
                                className="text-sm bg-gray-700 px-2 py-1 rounded hover:bg-gray-600"
                            >
                                16:9
                            </button>
                            <button
                                onClick={() => setCropRegion({ x: 12.5, y: 12.5, width: 75, height: 75 })}
                                className="text-sm bg-gray-700 px-2 py-1 rounded hover:bg-gray-600"
                            >
                                1:1
                            </button>
                        </div>
                    </div>
                );
                
            case 'extend':
                return (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-purple-300">Extend / Trim Video</h3>
                        <div>
                            <label className="block text-sm font-medium mb-2">Start Point: {trimStart}%</label>
                            <input
                                type="range"
                                min="0"
                                max="50"
                                value={trimStart}
                                onChange={(e) => setTrimStart(parseInt(e.target.value))}
                                className="w-full accent-green-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">End Point: {trimEnd}%</label>
                            <input
                                type="range"
                                min="50"
                                max="100"
                                value={trimEnd}
                                onChange={(e) => setTrimEnd(parseInt(e.target.value))}
                                className="w-full accent-red-500"
                            />
                        </div>
                        <p className="text-sm text-gray-400">
                            Duration: {((trimEnd - trimStart) / 100 * duration).toFixed(1)}s
                        </p>
                        <div className="pt-4 border-t border-gray-700">
                            <label className="block text-sm font-medium mb-2">AI Extend (describe what happens next)</label>
                            <textarea
                                value={editPrompt}
                                onChange={(e) => setEditPrompt(e.target.value)}
                                placeholder="Describe how the video should continue..."
                                className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg resize-none h-20"
                            />
                        </div>
                    </div>
                );
                
            case 'animate':
                return (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-purple-300">Add Animation</h3>
                        <div>
                            <label className="block text-sm font-medium mb-2">Animation Type</label>
                            <div className="grid grid-cols-2 gap-2">
                                {['Ken Burns', 'Slow Zoom', 'Pan Left', 'Pan Right', 'Shake', 'Pulse'].map(anim => (
                                    <button
                                        key={anim}
                                        onClick={() => setEditPrompt(anim)}
                                        className={`py-2 px-3 rounded text-sm ${
                                            editPrompt === anim 
                                                ? 'bg-purple-600 text-white' 
                                                : 'bg-gray-700 hover:bg-gray-600'
                                        }`}
                                    >
                                        {anim}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">Custom Animation Prompt</label>
                            <textarea
                                value={editPrompt}
                                onChange={(e) => setEditPrompt(e.target.value)}
                                placeholder="Describe the animation effect you want..."
                                className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg resize-none h-20"
                            />
                        </div>
                    </div>
                );
                
            case 'addElement':
                return (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-purple-300">Add Element</h3>
                        <div>
                            <label className="block text-sm font-medium mb-2">Element Type</label>
                            <div className="grid grid-cols-3 gap-2">
                                {['Text', 'Image', 'Shape', 'Sticker', 'Logo', 'Effect'].map(type => (
                                    <button
                                        key={type}
                                        onClick={() => setSelectedElement(type)}
                                        className={`py-2 px-3 rounded text-sm ${
                                            selectedElement === type 
                                                ? 'bg-purple-600 text-white' 
                                                : 'bg-gray-700 hover:bg-gray-600'
                                        }`}
                                    >
                                        {type}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">Describe the element to add</label>
                            <textarea
                                value={editPrompt}
                                onChange={(e) => setEditPrompt(e.target.value)}
                                placeholder="E.g., 'Add a glowing particle effect around the product' or 'Add text: Limited Time Offer'"
                                className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg resize-none h-20"
                            />
                        </div>
                    </div>
                );
                
            case 'eraseElement':
                return (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-purple-300">Erase Element</h3>
                        <p className="text-sm text-gray-400">
                            Use AI to remove unwanted objects or elements from the video.
                        </p>
                        <div>
                            <label className="block text-sm font-medium mb-2">What to erase</label>
                            <textarea
                                value={editPrompt}
                                onChange={(e) => setEditPrompt(e.target.value)}
                                placeholder="Describe what you want to remove, e.g., 'Remove the watermark in the corner' or 'Erase the person in the background'"
                                className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg resize-none h-24"
                            />
                        </div>
                        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-sm text-yellow-300">
                            💡 Tip: Be specific about the location and appearance of the element you want to remove.
                        </div>
                    </div>
                );
                
            case 'replace':
                return (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-purple-300">Replace Character/Element</h3>
                        {projectData?.characters?.length > 0 && (
                            <div>
                                <label className="block text-sm font-medium mb-2">Select character to replace with</label>
                                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                                    {projectData.characters.map(char => (
                                        <button
                                            key={char.id}
                                            onClick={() => setSelectedElement(char)}
                                            className={`py-2 px-3 rounded text-sm text-left flex items-center gap-2 ${
                                                selectedElement?.id === char.id 
                                                    ? 'bg-purple-600 text-white' 
                                                    : 'bg-gray-700 hover:bg-gray-600'
                                            }`}
                                        >
                                            {char.image && (
                                                <img src={char.image} alt="" className="w-6 h-6 rounded object-cover" />
                                            )}
                                            <span className="truncate">{char.name}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium mb-2">Replacement instructions</label>
                            <textarea
                                value={editPrompt}
                                onChange={(e) => setEditPrompt(e.target.value)}
                                placeholder="Describe what to replace and how, e.g., 'Replace the product jar with the Viriya Cream Jar' or 'Change the character's outfit to a red dress'"
                                className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg resize-none h-24"
                            />
                        </div>
                    </div>
                );
                
            default:
                return null;
        }
    };
    
    return (
        <div 
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" 
            onClick={onClose}
        >
            <div 
                className="bg-[#181820] border border-gray-700 rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b border-gray-700">
                    <div>
                        <h2 className="text-xl font-bold">Video Editor</h2>
                        <p className="text-sm text-gray-400">Edit and enhance your video</p>
                    </div>
                    <button 
                        onClick={onClose} 
                        className="p-2 rounded-full hover:bg-gray-700"
                    >
                        <CloseIcon className="w-6 h-6" />
                    </button>
                </div>
                
                {/* Main Content */}
                <div className="flex-1 flex overflow-hidden">
                    {/* Left Sidebar - Tools */}
                    <div className="w-20 bg-gray-900/50 border-r border-gray-700 p-2 flex flex-col gap-1">
                        {EDIT_TOOLS.map(tool => (
                            <button
                                key={tool.id}
                                onClick={() => setActiveTool(tool.id)}
                                className={`p-3 rounded-lg flex flex-col items-center gap-1 transition-colors ${
                                    activeTool === tool.id 
                                        ? 'bg-purple-600 text-white' 
                                        : 'hover:bg-gray-700 text-gray-400 hover:text-white'
                                }`}
                                title={tool.description}
                            >
                                <tool.icon className="w-5 h-5" />
                                <span className="text-[10px] leading-tight text-center">{tool.name}</span>
                            </button>
                        ))}
                    </div>
                    
                    {/* Center - Video Preview */}
                    <div className="flex-1 flex flex-col">
                        <div className="flex-1 flex items-center justify-center bg-black p-4">
                            {shot.video ? (
                                <div className="relative max-w-full max-h-full">
                                    <video
                                        ref={videoRef}
                                        src={shot.video}
                                        className="max-w-full max-h-[50vh] rounded-lg"
                                        style={{
                                            transform: `scale(${zoomLevel / 100}) translate(${panX}%, ${panY}%)`,
                                            clipPath: activeTool === 'crop' 
                                                ? `inset(${cropRegion.y}% ${100 - cropRegion.x - cropRegion.width}% ${100 - cropRegion.y - cropRegion.height}% ${cropRegion.x}%)`
                                                : 'none'
                                        }}
                                        loop
                                    />
                                    {activeTool === 'crop' && (
                                        <div 
                                            className="absolute border-2 border-purple-500 border-dashed pointer-events-none"
                                            style={{
                                                left: `${cropRegion.x}%`,
                                                top: `${cropRegion.y}%`,
                                                width: `${cropRegion.width}%`,
                                                height: `${cropRegion.height}%`,
                                            }}
                                        />
                                    )}
                                </div>
                            ) : (
                                <div className="text-gray-500 text-center">
                                    <p className="text-lg">No video available</p>
                                    <p className="text-sm">Generate or upload a video first</p>
                                </div>
                            )}
                        </div>
                        
                        {/* Video Controls */}
                        {shot.video && (
                            <div className="p-4 border-t border-gray-700 bg-gray-900/50">
                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={handlePlayPause}
                                        className="p-2 bg-purple-600 rounded-full hover:bg-purple-700"
                                    >
                                        {isPlaying ? (
                                            <PauseIcon className="w-5 h-5" />
                                        ) : (
                                            <PlayIcon className="w-5 h-5" />
                                        )}
                                    </button>
                                    <span className="text-sm text-gray-400 w-16">
                                        {formatTime(currentTime)}
                                    </span>
                                    <div 
                                        className="flex-1 h-2 bg-gray-700 rounded-full cursor-pointer relative"
                                        onClick={handleSeek}
                                    >
                                        <div 
                                            className="absolute h-full bg-purple-500 rounded-full"
                                            style={{ width: `${(currentTime / duration) * 100}%` }}
                                        />
                                        {activeTool === 'extend' && (
                                            <>
                                                <div 
                                                    className="absolute h-full bg-green-500/50"
                                                    style={{ left: 0, width: `${trimStart}%` }}
                                                />
                                                <div 
                                                    className="absolute h-full bg-red-500/50"
                                                    style={{ left: `${trimEnd}%`, right: 0 }}
                                                />
                                            </>
                                        )}
                                    </div>
                                    <span className="text-sm text-gray-400 w-16 text-right">
                                        {formatTime(duration)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Right Sidebar - Tool Options */}
                    <div className="w-80 border-l border-gray-700 p-4 overflow-y-auto">
                        {renderToolPanel()}
                        
                        {activeTool && (
                            <div className="mt-6 pt-4 border-t border-gray-700">
                                {error && (
                                    <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-300 text-sm">
                                        {error}
                                    </div>
                                )}
                                <button
                                    onClick={handleApplyEdit}
                                    disabled={isProcessing}
                                    className="w-full py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 rounded-lg font-semibold flex items-center justify-center gap-2"
                                >
                                    {isProcessing ? (
                                        <>
                                            <LoadingSpinner />
                                            Processing...
                                        </>
                                    ) : (
                                        <>Apply {EDIT_TOOLS.find(t => t.id === activeTool)?.name}</>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Footer */}
                <div className="p-4 border-t border-gray-700 flex justify-between items-center">
                    <p className="text-sm text-gray-400">
                        Shot: {shot.title || `Shot ${shot.id?.substring(0, 8)}`}
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                // Save any pending changes and close
                                onClose();
                            }}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-semibold"
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
